import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, Loader2, Radio as RadioIcon } from "lucide-react";
import { useRadio } from "@/lib/RadioContext";
import { EPISODES } from "@/components/Derive";
import { CHANNEL_META } from "@/components/RadioChannels";

const L = {
  fr: { open: "Ouvrir la radio", stop: "Arrêter", jingle: "Radio Cocoon", live: "En direct" },
  en: { open: "Open the radio", stop: "Stop", jingle: "Radio Cocoon", live: "Live" },
  es: { open: "Abrir la radio", stop: "Detener", jingle: "Radio Cocoon", live: "En directo" },
  nl: { open: "Open de radio", stop: "Stoppen", jingle: "Radio Cocoon", live: "Live" },
};
const BY_SLUG = Object.fromEntries(EPISODES.map((e) => [e.slug, e]));

// Bulle flottante : la radio reste à l'écoute pendant la navigation
export const RadioBubble = () => {
  const r = useRadio();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = L[lang];
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => { if (r.playing) setActive(true); }, [r.playing]);
  if (!active || pathname === "/radio" || !r.pos) return null;

  const isJingle = r.pos.kind === "jingle";
  const ep = BY_SLUG[r.currentSlug];
  const title = isJingle || !ep ? T.jingle : ep.title[lang] || ep.title.fr;
  const cover = isJingle || !ep ? "/derive/radio/cover.jpg" : `/derive/covers/${ep.slug}.jpg`;
  const stop = () => { r.pause(); setActive(false); setOpen(false); };

  return (
    <div className="fixed right-4 bottom-44 md:bottom-24 z-[60] flex flex-col items-end gap-3" data-testid="radio-bubble">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="glass w-72 rounded-2xl border border-primary/25 p-4 shadow-2xl" data-testid="radio-bubble-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-red-300">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> {T.live} · {CHANNEL_META[r.channel].label[lang]}
              </span>
              <button onClick={() => setOpen(false)} aria-label="close" data-testid="radio-bubble-close" className="text-foreground/40 hover:text-foreground transition-colors"><X size={14} /></button>
            </div>
            <p className="font-display text-lg leading-snug mb-3 line-clamp-2" data-testid="radio-bubble-title">{title}</p>
            <div className="flex items-center gap-2">
              <button onClick={r.toggle} data-testid="radio-bubble-toggle"
                className="btn-frost-orange inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold">
                {r.loading ? <Loader2 size={13} className="animate-spin" /> : r.playing ? <Pause size={13} /> : <Play size={13} />}
              </button>
              <Link to="/radio" onClick={() => setOpen(false)} data-testid="radio-bubble-open"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 px-3.5 py-2 text-xs text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors">
                <RadioIcon size={13} /> {T.open}
              </Link>
              <button onClick={stop} data-testid="radio-bubble-stop" className="ml-auto text-xs text-foreground/45 hover:text-foreground/80 transition-colors">{T.stop}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setOpen((o) => !o)} aria-label="Radio Cocoon" data-testid="radio-bubble-button"
        className="relative h-14 w-14 rounded-full p-[2px] bg-gradient-to-br from-primary to-amber-300 shadow-[0_10px_40px_-10px_hsl(28_96%_56%_/_0.7)] transition-transform hover:scale-105">
        {r.playing && <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />}
        <img src={cover} alt={title} className={`relative h-full w-full rounded-full object-cover ${r.playing ? "animate-[spin_14s_linear_infinite]" : "opacity-70"}`} />
        <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-red-500 border-2 border-background" />
      </button>
    </div>
  );
};
